import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import FeedDonationForm from '../components/FeedDonationForm';
import FeedTransparency from '../components/FeedTransparency';
import { useAnimals } from '../hooks/useAnimals';
import './FeedTheCows.css';

const FeedTheCows = () => {
  const [scrollY, setScrollY] = useState(0);
  const { animals } = useAnimals(1); // Get one cow for hero image

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'Digital Gau-Seva – Feed the Cows with PharmWealth';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Make your seva count – sponsor real fodder, track impact, and feed cows ethically every day.');

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', 'Digital Gau-Seva – Feed the Cows with PharmWealth');

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', 'Make your seva count – sponsor real fodder, track impact, and feed cows ethically every day.');

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', 'https://pharmwealth.com/feed-the-cows');

    return () => {
      // Reset to default on unmount
      document.title = 'PharmWealth Ventures | Ethical Luxury Dairy & Innovation';
    };
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const scrollToForm = () => {
    document.getElementById('donation-form')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Get hero image - prefer real cow from farm, fallback to cow feeding image
  const heroImageUrl = animals.length > 0 && animals[0].photoUrl 
    ? animals[0].photoUrl 
    : 'https://images.unsplash.com/photo-1560493676-04071c5f467b?w=1920'; // Cow feeding on green grass

  return (
    <div className="feed-cows-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background parallax-container">
          <div 
            className="parallax-bg" 
            style={{
              transform: `translateY(${scrollY * 0.5}px)`,
              backgroundImage: `linear-gradient(to bottom, rgba(12, 26, 42, 0.7), rgba(12, 26, 42, 0.5), transparent), url(${heroImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>
        </div>
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-headline"
          >
            Feed the Cows, the Modern Way.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hero-subheadline"
          >
            Transform ancient seva into measurable impact. Sponsor real green fodder, know where it goes, and help cows eat better every day.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hero-ctas"
          >
            <button onClick={scrollToForm} className="btn btn-primary">
              Feed a Cow Today – ₹20
            </button>
            <button onClick={scrollToForm} className="btn btn-outline">
              Start Daily Seva – from ₹20/day
            </button>
            <button onClick={scrollToForm} className="btn btn-outline">
              Adopt a Cow for a Year
            </button>
          </motion.div>
        </div>
      </section>

      {/* Why Feeding Cows Matters */}
      <section className="why-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="why-content"
          >
            <h2 className="section-title">Why Feeding Cows Matters</h2>
            <div className="gold-line"></div>
            <div className="why-split">
              <div className="why-text">
                <p className="why-paragraph">
                  Feeding cows is both <em>dān</em> (charity) and <em>seva</em> (service). It nourishes beings that nourish us — and brings peace, health, and blessings.
                </p>
                <p className="why-paragraph">
                  In Hindu tradition, the cow (Gau Mata) is revered as a symbol of abundance, motherhood, and dharma. Feeding cows is considered one of the most meritorious acts, believed to bring spiritual merit, remove obstacles, and invite prosperity.
                </p>
                <p className="why-paragraph">
                  Beyond the spiritual, there's a practical truth: well-fed cows produce better milk, live healthier lives, and contribute to sustainable farming. Your contribution directly supports ethical dairy practices and animal welfare.
                </p>
              </div>
              <div className="why-visual">
                <div className="cow-illustration">
                  🐄
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Problem with Temple Fodder */}
      <section className="problem-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="problem-content"
          >
            <h2 className="section-title">The Problem with Temple Fodder</h2>
            <div className="gold-line"></div>
            <div className="problem-grid">
              <div className="problem-item">
                <div className="problem-icon">💰</div>
                <h3>Overpriced</h3>
                <p>Temple fodder costs ₹15–20/kg, while farm-sourced green fodder costs only ₹3–6/kg at scale.</p>
              </div>
              <div className="problem-item">
                <div className="problem-icon">📦</div>
                <h3>Tiny Bundles</h3>
                <p>Small bundles sold at temples don't provide real nutrition — a cow needs 15–20 kg of green fodder daily.</p>
              </div>
              <div className="problem-item">
                <div className="problem-icon">❓</div>
                <h3>No Visibility</h3>
                <p>You never know which animal actually benefits from your donation, or if it reaches them at all.</p>
              </div>
            </div>
            <div className="problem-conclusion">
              <p><strong>PharmWealth bridges intent and impact.</strong> Your donation goes directly to real fodder, fed to real cows, with verified updates.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Model — More Seva per Rupee */}
      <section className="model-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="model-content"
          >
            <h2 className="section-title">Our Model — More Seva per Rupee</h2>
            <div className="gold-line"></div>
            <div className="model-cards">
              <div className="model-card card card-gold">
                <div className="model-amount">₹20</div>
                <h3>One-Time Feeding</h3>
                <ul className="model-benefits">
                  <li>~3–4 kg green fodder</li>
                  <li>Direct farm sourcing</li>
                  <li>Fed to real cows today</li>
                </ul>
              </div>
              <div className="model-card card card-gold">
                <div className="model-amount">₹50</div>
                <h3>Daily Support</h3>
                <ul className="model-benefits">
                  <li>~8–10 kg green fodder</li>
                  <li>Nutritional balance</li>
                  <li>Photo update included</li>
                </ul>
              </div>
              <div className="model-card card card-gold">
                <div className="model-amount">₹100</div>
                <h3>Premium Seva</h3>
                <ul className="model-benefits">
                  <li>~15–20 kg green fodder</li>
                  <li>Full daily ration</li>
                  <li>Video update + certificate</li>
                </ul>
              </div>
            </div>
            <p className="model-note">
              All donations are allocated transparently. We source fodder directly from our farm network at wholesale rates, ensuring maximum impact per rupee.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How Much One Cow Really Needs */}
      <section className="needs-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="needs-content"
          >
            <h2 className="section-title">How Much One Cow Really Needs</h2>
            <div className="gold-line"></div>
            <div className="needs-infographic">
              <div className="needs-item">
                <div className="needs-icon">🌿</div>
                <div className="needs-detail">
                  <h3>15–20 kg</h3>
                  <p>Green Fodder per Day</p>
                </div>
              </div>
              <div className="needs-item">
                <div className="needs-icon">🌾</div>
                <div className="needs-detail">
                  <h3>4–6 kg</h3>
                  <p>Dry Fodder + Minerals</p>
                </div>
              </div>
              <div className="needs-item">
                <div className="needs-icon">💰</div>
                <div className="needs-detail">
                  <h3>₹30–₹60</h3>
                  <p>Daily Green Fodder Cost (Farm Level)</p>
                </div>
              </div>
            </div>
            <p className="needs-note">
              Your ₹20 may sound small — but at farm efficiency it feeds a meaningful part of a cow's day. Combined with other donations, we ensure every cow gets proper nutrition.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Seva Options */}
      <section className="options-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="options-content"
          >
            <h2 className="section-title">Choose Your Seva</h2>
            <div className="gold-line"></div>
            <div className="options-grid">
              <div className="option-card card card-gold">
                <h3>One-Time Feeding</h3>
                <div className="option-amounts">
                  <div className="amount-option">₹20</div>
                  <div className="amount-option">₹50</div>
                  <div className="amount-option">₹100</div>
                </div>
                <p className="option-description">Feed a cow today. Choose any amount above ₹20.</p>
                <ul className="option-features">
                  <li>~3–20 kg fodder (based on amount)</li>
                  <li>One-time contribution</li>
                  <li>Email confirmation</li>
                </ul>
              </div>
              <div className="option-card card card-gold">
                <h3>Daily Seva Subscription</h3>
                <div className="option-amounts">
                  <div className="amount-option">₹20/day</div>
                  <div className="amount-option">₹50/day</div>
                </div>
                <p className="option-description">Auto-debit small amount daily. Build a habit of seva.</p>
                <ul className="option-features">
                  <li>Recurring daily donation</li>
                  <li>Monthly impact report</li>
                  <li>Cancel anytime</li>
                </ul>
              </div>
              <div className="option-card card card-gold">
                <h3>Monthly Support</h3>
                <div className="option-amounts">
                  <div className="amount-option">₹500/month</div>
                  <div className="amount-option">₹1,100/month</div>
                </div>
                <p className="option-description">Sustained monthly contribution for consistent feeding.</p>
                <ul className="option-features">
                  <li>~15–30 days of feeding</li>
                  <li>Quarterly photo updates</li>
                  <li>Priority support</li>
                </ul>
              </div>
              <div className="option-card card card-gold">
                <h3>Year-Round Adoption</h3>
                <div className="option-amounts">
                  <div className="amount-option">₹7,300/year</div>
                </div>
                <p className="option-description">Adopt a cow for a full year. 365 days of feeding.</p>
                <ul className="option-features">
                  <li>Full year commitment</li>
                  <li>Monthly photo/video updates</li>
                  <li>Certificate of adoption</li>
                  <li>Name the cow (optional)</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Digital Gau-Seva Transparency */}
      <section className="transparency-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="transparency-content"
          >
            <h2 className="section-title">Digital Gau-Seva Transparency</h2>
            <div className="gold-line"></div>
            <div className="transparency-split">
              <div className="transparency-text">
                <p>
                  Every donation is recorded in our system and converted into real feed events. We maintain detailed logs of:
                </p>
                <ul className="transparency-list">
                  <li>Date and time of feeding</li>
                  <li>Amount of fodder allocated</li>
                  <li>Which cows received the feed</li>
                  <li>Photos and videos of feeding sessions</li>
                </ul>
                <p>
                  Donors receive email confirmations and can request updates on their contributions. We believe seva should be transparent, not mysterious.
                </p>
              </div>
              <div className="transparency-visual">
                <div className="photo-carousel-placeholder">
                  <p>📸 Real Cows Fed This Week</p>
                  <p className="carousel-note">Photo gallery coming soon</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transparency Promise Section */}
      <section className="transparency-promise-section section">
        <div className="container">
          <FeedTransparency />
        </div>
      </section>

      {/* Contribution Form */}
      <section id="donation-form" className="form-section section">
        <div className="container">
          <FeedDonationForm />
        </div>
      </section>

      {/* Call-to-Action Block */}
      <section className="cta-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="cta-block"
          >
            <h2>Turn a Small Daily Habit into Lifelong Gau-Seva</h2>
            <p>
              Whether you are in Bengaluru, Mumbai, Delhi, or overseas, your ₹20 can become real fodder today. Join our mission to feed with faith and efficiency.
            </p>
            <div className="cta-buttons">
              <button onClick={scrollToForm} className="btn btn-primary">
                Feed Now
              </button>
              <button onClick={scrollToForm} className="btn btn-outline">
                Start Daily Seva
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FeedTheCows;

